import React, { useState } from "react";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MaterialButton from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import EvaluationCards from "../Components/EvaluationCard";
import CircularProgress from '@mui/material/CircularProgress';
import { getEvaluations as fetchEvaluations, getUsersByGroup } from '../services/UserService';
import formatDate from '../utils/formatDate';

const evaluationSelectSx = {
    width: '100%',
    boxSizing: 'border-box',
    height: 48,
    borderRadius: '14px',
    color: '#17233D',
    fontSize: 16,
    backgroundColor: '#FFFFFF',
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#9FC8DA',
        borderWidth: '3px',
        borderRadius: '14px',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#9FC8DA',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#9FC8DA',
    },
};

const evaluationMenuProps = {
    PaperProps: {
        sx: {
            mt: 0.75,
            border: '2px solid #9FC8DA',
            borderRadius: '12px',
            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.16)',
            '& .MuiMenuItem-root': {
                minHeight: 40,
                mx: 0.5,
                px: 2,
                borderRadius: '8px',
                color: '#17233D',
                fontSize: 15,
            },
            '& .MuiMenuItem-root:hover': {
                backgroundColor: '#F1FAFD',
            },
            '& .MuiMenuItem-root.Mui-selected': {
                backgroundColor: '#E5F5FA',
                fontWeight: 600,
            },
            '& .MuiMenuItem-root.Mui-selected:hover': {
                backgroundColor: '#D9F0F7',
            },
        },
    },
};


export default function Evaluation() {

    const [arrivalDate, setArrivalDate] = useState('');
    const [group, setGroup] = React.useState('');
    const [arrivalDates, setArrivalDates] = useState([]);
    const [isLoadingDates, setIsLoadingDates] = useState(false);
    const [shownData, setShownData] = useState([])
    const [value, setValue] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    const selectHandleChange = (event) => {
        setGroup(event.target.value);
        setArrivalDate('');
        setArrivalDates([]);
        setShownData([]);
        setHasSearched(false);
        setError('');
    };

    React.useEffect(() => {
        if (!group) return undefined;

        let isCurrentRequest = true;
        setIsLoadingDates(true);

        getUsersByGroup(group)
            .then((result) => {
                if (!isCurrentRequest) return;

                const startOfToday = new Date();
                startOfToday.setHours(0, 0, 0, 0);

                const arrivalDateByDay = new Map();
                (result?.Items || [])
                    .filter((user) => {
                        if (!user.arrivalDate || !user.departureDate) return false;
                        const departureDate = new Date(user.departureDate);
                        return !Number.isNaN(departureDate.getTime()) && departureDate < startOfToday;
                    })
                    .map((user) => new Date(user.arrivalDate))
                    .filter((date) => !Number.isNaN(date.getTime()))
                    .sort((firstDate, secondDate) => secondDate - firstDate)
                    .forEach((date) => {
                        const dayKey = formatDate(date);
                        if (!arrivalDateByDay.has(dayKey)) {
                            arrivalDateByDay.set(dayKey, date.toISOString());
                        }
                    });

                const uniqueArrivalDates = [...arrivalDateByDay.values()].slice(0, 10);

                setArrivalDates(uniqueArrivalDates);
            })
            .catch(() => {
                if (isCurrentRequest) setArrivalDates([]);
            })
            .finally(() => {
                if (isCurrentRequest) setIsLoadingDates(false);
            });

        return () => {
            isCurrentRequest = false;
        };
    }, [group]);


    const getEvaluations = async () => {
        if (!group || !arrivalDate) {
            setError('Velg ankomstdato og gruppe.');
            setShownData([]);
            return;
        }

        setIsLoading(true);
        setError('');
        setShownData([]);

        try {
            const results = await fetchEvaluations({
                arrivalDate,
                groupId: group,
            });

            if (!Array.isArray(results)) {
                setShownData([]);
                setError('Kunne ikke hente evalueringene. Prøv igjen.');
                return;
            }

            setShownData(results);
            setHasSearched(true);
        } catch {
            setShownData([]);
            setError('Kunne ikke hente evalueringene. Prøv igjen.');
        } finally {
            setIsLoading(false);
        }
    }

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };


    return (
        <Box sx={{ minHeight: '100%', bgcolor: '#F7FAFC', px: { xs: 2, md: 8 }, py: { xs: 3, md: 4 } }}>
            <Box sx={{ maxWidth: 1018, mx: 'auto' }}>
                <Typography sx={{ color: '#17233D', fontSize: { xs: 30, md: 34 }, fontWeight: 700, lineHeight: 1.1 }}>
                    Evaluering
                </Typography>
                <Typography sx={{ mt: 1, color: '#647B9C', fontSize: 16 }}>
                    Legg inn ankomstdato og gruppenummer for å hente evaluering
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' }, gap: 2, mt: 3 }}>
                    <Box sx={{ width: '100%', textAlign: 'left' }}>
                        <Typography sx={{ mb: 0.5, color: '#17233D', fontSize: 15, fontWeight: 500, display: 'block' }}>
                            Gruppe
                        </Typography>
                        <FormControl fullWidth size="small" sx={{ m: 0 }}>
                            <Select
                                labelId="evaluation-group-label"
                                id="evaluation-group"
                                value={group}
                                label=""
                                onChange={selectHandleChange}
                                sx={evaluationSelectSx}
                                MenuProps={evaluationMenuProps}
                                startAdornment={<InputAdornment position="start"><GroupsOutlinedIcon sx={{ color: '#31557D', fontSize: 22 }} /></InputAdornment>}
                            >
                                <MenuItem value="2C">2C</MenuItem>
                                <MenuItem value="2D">2D</MenuItem>
                                <MenuItem value="2E">2E</MenuItem>
                                <MenuItem value="1A">1A</MenuItem>
                                <MenuItem value="1B">1B</MenuItem>
                                <MenuItem value="TEST">TEST</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Box sx={{ width: '100%', textAlign: 'left' }}>
                        <Typography sx={{ mb: 0.5, color: '#17233D', fontSize: 15, fontWeight: 500, display: 'block' }}>
                            Ankomstdato
                        </Typography>
                        <FormControl fullWidth size="small" sx={{ m: 0 }} disabled={!group || isLoadingDates || arrivalDates.length === 0}>
                            <Select
                                labelId="evaluation-arrival-date-label"
                                id="evaluation-arrival-date"
                                value={arrivalDate}
                                label=""
                                onChange={(event) => setArrivalDate(event.target.value)}
                                sx={evaluationSelectSx}
                                MenuProps={evaluationMenuProps}
                                startAdornment={<InputAdornment position="start"><CalendarTodayOutlinedIcon sx={{ color: '#31557D', fontSize: 20 }} /></InputAdornment>}
                            >
                                {arrivalDates.map((date) => (
                                    <MenuItem key={date} value={date}>
                                        {formatDate(date)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <MaterialButton
                        onClick={getEvaluations}
                        disabled={isLoading || isLoadingDates || !group || !arrivalDate}
                        variant="contained"
                        startIcon={<DescriptionOutlinedIcon />}
                        sx={{ alignSelf: 'end', minWidth: 230, height: 48, borderRadius: '14px', bgcolor: '#BDECF3', color: '#087D9A', fontWeight: 700, boxShadow: 'none', '&:hover': { bgcolor: '#A9E3EC', boxShadow: 'none' } }}
                    >
                        {isLoading || isLoadingDates ? <CircularProgress size={22} sx={{ color: '#087D9A' }} /> : 'Hent evaluering'}
                    </MaterialButton>
                </Box>

                {error && <Typography role="alert" sx={{ mt: 2, color: '#B42318', fontSize: 14 }}>{error}</Typography>}

                <Tabs
                    value={value}
                    onChange={handleChange}
                    centered
                    sx={{ mt: 3, minHeight: 46, '& .MuiTabs-indicator': { bgcolor: '#23ABC7', height: 2 }, '& .MuiTab-root': { minHeight: 46, px: 3, color: '#64748B', fontSize: 14, fontWeight: 600, textTransform: 'uppercase' }, '& .Mui-selected': { color: '#119DB9' } }}
                >
                    <Tab label="Barn" />
                    <Tab label="Foresatte" />
                </Tabs>

                <Box sx={{ mt: 2 }}>
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                            <CircularProgress size={30} sx={{ color: '#23ABC7' }} />
                        </Box>
                    ) : !error && shownData.length > 0 ? (
                        value === 0
                            ? <EvaluationCards data={shownData} type="child" />
                            : <EvaluationCards data={shownData} type="parent" />
                    ) : !error && hasSearched ? (
                        <Typography sx={{ py: 5, textAlign: 'center', color: '#64748B', fontSize: 14 }}>
                            Ingen evalueringer funnet for valgt dato og gruppe.
                        </Typography>
                    ) : !error ? (
                        <Typography sx={{ py: 5, textAlign: 'center', color: '#64748B', fontSize: 14 }}>
                            Velg ankomstdato og gruppe for å hente evalueringer.
                        </Typography>
                    ) : null}
                </Box>
            </Box>
        </Box>
    )
}